/**
 * EightDControlsPanel - 8D Spatial Audio Controls
 * Controls for 8D effect parameters
 */
import React, { memo } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Lock, Crown, ChevronDown } from 'lucide-react';
import useCollapsePref from '../hooks/useCollapsePref';

const EightDControlsPanel = memo(({
  isEnabled,
  isPremium,
  hasAudioSource,
  pathType,
  panningSpeed,
  radius,
  reverbMix,
  clarityBoost,
  performanceMode,
  onToggle,
  onPathTypeChange,
  onPanningSpeedChange,
  onRadiusChange,
  onReverbMixChange,
  onClarityBoostChange,
  onPerformanceModeToggle,
}) => {
  const [open, toggleOpen] = useCollapsePref('8d_audio', false);
  const getSpeedLabel = (speed) => {
    if (speed < 0.15) return '🐌 Very Slow';
    if (speed < 0.4) return '🚶 Slow';
    if (speed < 0.8) return '🏃 Normal';
    if (speed < 1.5) return '⚡ Fast';
    return '🚀 Very Fast';
  };

  return (
    <div className="space-y-2 mt-4 pt-4 border-t border-white/20">
      {/* Header — click to expand/collapse */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left"
        data-testid="eightd-collapse-toggle"
      >
        <span className="text-white text-sm flex items-center gap-2">
          8D Audio Effect
          {!isPremium && <Crown className="w-4 h-4 text-yellow-400" />}
          {isEnabled && !open && <span className="text-purple-300 text-[10px]">ON</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <div className={open ? 'space-y-2' : 'hidden'}>
      {/* Toggle Button */}
      <div className="flex items-center gap-2">
        <Button
          onClick={onToggle}
          disabled={!hasAudioSource}
          data-testid="eightd-toggle-btn"
          className={`${isEnabled && isPremium 
            ? 'bg-purple-600 hover:bg-purple-700' 
            : 'bg-gray-600 hover:bg-gray-700'} relative flex items-center gap-2`}
        >
          {!isPremium && <Lock className="w-4 h-4 text-yellow-400" />}
          {isEnabled ? '8D ON' : '8D OFF'}
          {!isPremium && <Crown className="w-4 h-4 text-yellow-400" />}
        </Button>
        <div className="text-white/70 text-xs">
          {isPremium 
            ? (isEnabled ? 'Immersive 3D spatial audio' : 'Standard stereo audio')
            : 'Premium feature'
          }
        </div>
      </div>

      {/* 8D Controls - Only show when enabled */}
      {isEnabled && isPremium && (
        <div className="mt-3 p-3 bg-purple-500/20 border border-purple-500/30 rounded-lg space-y-3">
          <div className="text-white text-xs font-medium mb-1 flex items-center gap-2">
            <span>8D Audio Effect</span>
            <span className="text-purple-300">🎧</span>
          </div>
          <div className="text-white/70 text-xs mb-2">
            True 8D circular panning with HRTF spatial audio + subtle reverb
          </div>

          {/* Instruction */}
          <div className="bg-blue-500/20 border border-blue-400/30 rounded p-2 mb-3">
            <div className="text-blue-200 text-xs space-y-1">
              <div>✨ <strong>Real-time:</strong> All settings update instantly!</div>
              <div>🎧 <strong>Audio glitchy?</strong> Switch to "Low CPU Mode" below</div>
            </div>
          </div>

          {/* 8D Path Selection */}
          <div className="space-y-2">
            <label className="text-white text-xs font-medium">8D Path Type</label>
            <Select value={pathType} onValueChange={onPathTypeChange}>
              <SelectTrigger className="bg-gray-800 border-purple-500/30 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-purple-500/30">
                <SelectItem value="circular" className="text-white">🔄 Circular Orbit</SelectItem>
                <SelectItem value="figure8" className="text-white">∞ Figure-8 (Smooth)</SelectItem>
                <SelectItem value="organic" className="text-white">🌊 Organic Drift</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Speed Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-xs font-medium">Speed (°/s)</label>
              <span className="text-purple-300 text-xs">{getSpeedLabel(panningSpeed[0])}</span>
            </div>
            <Slider
              value={panningSpeed}
              onValueChange={onPanningSpeedChange}
              max={2.0}
              min={0.05}
              step={0.05}
              className="text-purple-400"
            />
            <div className="flex justify-between text-white/60 text-xs">
              <span>Very Slow</span>
              <span className="text-purple-300">{panningSpeed[0].toFixed(2)}x</span>
              <span>Very Fast</span>
            </div>
          </div>

          {/* Radius Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-xs font-medium">Radius (m)</label>
              <span className="text-purple-300 text-xs">{radius[0].toFixed(2)}m</span>
            </div>
            <Slider
              value={radius}
              onValueChange={onRadiusChange}
              max={0.5}
              min={0.1}
              step={0.01}
              className="text-purple-400"
            />
            <div className="flex justify-between text-white/60 text-xs">
              <span>Subtle</span>
              <span>Wide</span>
            </div>
          </div>

          {/* Reverb Mix Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-xs font-medium">Reverb Mix</label>
              <span className="text-purple-300 text-xs">{Math.round(reverbMix[0] * 30)}%</span>
            </div>
            <Slider
              value={reverbMix}
              onValueChange={onReverbMixChange}
              max={1.0}
              min={0.0}
              step={0.05}
              className="text-purple-400"
            />
            <div className="flex justify-between text-white/60 text-xs">
              <span>Dry</span>
              <span>Wet</span>
            </div>
          </div>

          {/* Clarity Boost Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-xs font-medium flex items-center gap-1">
                ✨ Clarity Boost
                <span className="text-white/50 text-[10px]">(High-Freq)</span>
              </label>
              <span className="text-purple-300 text-xs">+{clarityBoost[0].toFixed(1)} dB</span>
            </div>
            <Slider
              value={clarityBoost}
              onValueChange={onClarityBoostChange}
              max={6.0}
              min={0.0}
              step={0.5}
              className="text-purple-400"
            />
            <div className="flex justify-between text-white/60 text-xs">
              <span>Warm</span>
              <span>Bright</span>
            </div>
          </div>

          {/* CPU Mode Toggle */}
          <div className="space-y-2 pt-2 border-t border-purple-500/20">
            <div className="flex items-center justify-between">
              <label className="text-white text-xs font-medium">CPU Usage</label>
              <span className="text-purple-300 text-xs">
                {performanceMode ? '🚀 Low CPU' : '💎 Full Quality'}
              </span>
            </div>
            <Button
              onClick={onPerformanceModeToggle}
              className={`${performanceMode 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-purple-600 hover:bg-purple-700'} w-full text-xs py-1`}
            >
              {performanceMode ? 'Switch to Full Quality' : 'Switch to Low CPU Mode'}
            </Button>
            <div className="text-white/60 text-xs">
              {performanceMode 
                ? 'Lighter processing, no reverb (recommended for phones)' 
                : 'Full HRTF + reverb (best for desktop/headphones)'}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
});

EightDControlsPanel.displayName = 'EightDControlsPanel';

export default EightDControlsPanel;
